import { Button } from '@/components/ui/button';
import { CartItemsResponse, deleteCartItems, updateCartItems } from '@/lib/collections/cartItems';
import { Increment } from '@/lib/db-client';
import { Minus, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

interface CartItemRowProps {
  item: CartItemsResponse;
}

export const CartItemRow: React.FC<CartItemRowProps> = ({ item }) => {
  const [busy, setBusy] = useState(false);

  const quantity = item.quantity ?? 0;
  const lineTotal = (item.price ?? 0) * quantity;

  const handleQuantityChange = async (delta: 1 | -1) => {
    if (delta === -1 && quantity <= 1) return;
    setBusy(true);
    try {
      const success = await updateCartItems(item.id, { quantity: Increment.by(delta) });
      if (!success) toast.error('Could not update quantity.');
    } catch (error) {
      console.error('Failed to update cart item quantity', error);
      toast.error('Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      const success = await deleteCartItems(item.id);
      if (success) {
        toast.success('Removed from cart.');
      } else {
        toast.error('Could not remove item.');
      }
    } catch (error) {
      console.error('Failed to remove cart item', error);
      toast.error('Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='flex items-center gap-4 rounded-2xl border border-border bg-card p-4'>
      <div className='h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-secondary'>
        {item.productPhoto ? (
          <img
            src={item.productPhoto}
            alt={item.productName}
            className='h-full w-full object-cover'
          />
        ) : (
          <div className='flex h-full w-full items-center justify-center text-xs font-bold text-primary'>
            {item.productName?.slice(0, 2).toUpperCase() ?? '?'}
          </div>
        )}
      </div>

      <div className='min-w-0 flex-1'>
        <p className='truncate font-heading text-sm font-bold text-foreground'>
          {item.productName}
        </p>
        {item.variant && <p className='mt-0.5 text-xs text-muted-foreground'>{item.variant}</p>}
        <p className='mt-1 text-sm font-semibold text-primary'>
          ₦{(item.price ?? 0).toLocaleString('en-NG')}
        </p>
      </div>

      <div className='flex items-center gap-1.5 rounded-full border border-border bg-background px-1.5 py-1'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 rounded-full disabled:opacity-40'
          disabled={busy || quantity <= 1}
          onClick={() => handleQuantityChange(-1)}
          aria-label='Decrease quantity'
        >
          <Minus className='h-3.5 w-3.5' />
        </Button>
        <span className='w-6 text-center text-sm font-semibold text-foreground'>{quantity}</span>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 rounded-full'
          disabled={busy}
          onClick={() => handleQuantityChange(1)}
          aria-label='Increase quantity'
        >
          <Plus className='h-3.5 w-3.5' />
        </Button>
      </div>

      <p className='w-24 shrink-0 text-right font-heading text-sm font-bold text-foreground'>
        ₦{lineTotal.toLocaleString('en-NG')}
      </p>

      <Button
        variant='ghost'
        size='icon'
        className='shrink-0 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
        disabled={busy}
        onClick={handleRemove}
        aria-label='Remove item'
      >
        <Trash2 className='h-4 w-4' />
      </Button>
    </div>
  );
};

export default CartItemRow;
